import { z } from 'zod'

export const TASK_STATES = ['inbox', 'next', 'waiting', 'someday', 'done', 'dropped'] as const
export type TaskState = (typeof TASK_STATES)[number]
export const OPEN_STATES: TaskState[] = ['inbox', 'next', 'waiting', 'someday']

const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'use YYYY-MM-DD')

export const checklistItem = z.object({ text: z.string(), done: z.boolean().default(false) }).strict()
export type ChecklistItem = z.infer<typeof checklistItem>

export const taskSpec = z
  .object({
    title: z.string().min(1, 'title is required').max(500),
    icon: z.string().max(40).optional(),
    notes: z.string().optional(),
    due: date.optional(),
    scheduled: date.optional(),
    scheduled_time: z
      .string()
      .regex(/^[0-2][0-9]:[0-5][0-9]$/, 'use HH:MM')
      .optional(),
    remind: z.boolean().default(false),
    /** one-off reminder, RFC 3339 UTC; the server pushes at that time */
    remind_at: z.string().optional(),
    /** meds-style: re-remind every N minutes until done; templates retire yesterday's copy as missed */
    nag_minutes: z.number().int().min(5).max(240).optional(),
    /** a here-and-there job: stale this many days after done_at (else creation) and pushed once */
    every_days: z.number().int().min(1).max(60).optional(),
    priority: z.number().int().min(0).max(3).default(0),
    estimate_minutes: z.number().int().min(1).optional(),
    recurrence: z.string().optional(),
    checklist: z.array(checklistItem).optional(),
    /** a task this one waits for: blocked while that one is open */
    after: z.string().optional(),
  })
  .strict()
export type TaskSpec = z.infer<typeof taskSpec>

export const session = z.object({ start: z.string(), end: z.string() })
export type Session = z.infer<typeof session>

export const taskStatus = z.object({
  state: z.enum(TASK_STATES),
  completed_at: z.string().optional(),
  dropped_reason: z.string().optional(),
  started_at: z.string().optional(),
  actual_minutes: z.number().int().default(0),
  sessions: z.array(session).optional(),
  last_touched: z.string(),
  template_id: z.string().optional(),
  next_due: z.string().optional(),
  reschedule_count: z.number().int().default(0),
  reminded_at: z.string().optional(),
  reminder_fired_for: z.string().optional(),
  /** what this task became (project, page, list); it is dropped with it */
  converted_to: z.object({ kind: z.string(), id: z.string() }).strict().optional(),
  /** kept by the reconciler: finished steps, of how many */
  steps_done: z.number().int().optional(),
  steps_total: z.number().int().optional(),
  /** local day a job (every_days) was last done with done_now; the task stays open */
  done_at: date.optional(),
  /** the baseline (done_at, else creation day) the one stale push was sent for */
  stale_reminded_for: date.optional(),
})
export type TaskStatus = z.infer<typeof taskStatus>

export const isOpen = (state: string) => state !== 'done' && state !== 'dropped'
export const isTemplate = (spec: { recurrence?: string }) => Boolean(spec.recurrence)
